import { useForm } from "react-hook-form";
import useWebsites from "../../data/useWebsites";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { IWebsite } from "../../models/Website";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { useEffect, useState } from "react";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { TextInput } from "../shared/Inputs/TextInput";
import useSendingDomains from "../../data/useSendingDomains";
import useNotification from "../notifications/useNotifications";
import { ApiError } from "../../hooks/useApiHelper";

interface WebsiteModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  website?: IWebsite | undefined;
}

export const FONT_OPTIONS = [
  { value: "poppins",    label: "Poppins (default)" },
  { value: "inter",      label: "Inter" },
  { value: "nunito",     label: "Nunito" },
  { value: "lato",       label: "Lato" },
  { value: "montserrat", label: "Montserrat" },
];

type FormValues = {
  domain: string;
  resumeDomain: string;
  faqUrl: string | null;
  googleTag: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  accentTextColor: string | null;
  bodyTextColor: string | null;
  fontFamily: string | null;
  sendingDomain: string;
  sendingFromLocalPart: string | null;
  sendingFromName: string | null;
}

// Value used in the sending-domain select for "send from the Wilma platform address".
const NO_SENDING_DOMAIN = "";

export default function AddEditWebsiteModal({ open, setOpen, website }: WebsiteModalProps) {
  const { deleteHandlers, saveHandlers } = useDefaultCRUDHandlers("Website");
  const { createWebsite, updateWebsite, deleteWebsite } = useWebsites();
  const { sendingDomains } = useSendingDomains();
  const { addNotification } = useNotification();
  const { register, handleSubmit, reset, watch, getValues, setError, formState: { errors } } = useForm<FormValues>();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const primaryColor = watch("primaryColor");
  const sendingDomain = watch("sendingDomain");
  const sendingFromLocalPart = watch("sendingFromLocalPart");
  const googleTagPattern = /^GTM-[A-Z0-9]+$/;
  const localPartPattern = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/i;

  const domainOptions = sendingDomains.data?.data ?? [];
  const selectedDomain = domainOptions.find((d) => d.domain === sendingDomain);
  // A website may still reference a domain that was since removed in core; keep it selectable so
  // the user can see (and clear) it rather than having the form silently drop it.
  const orphanedDomain = sendingDomain && !selectedDomain ? sendingDomain : null;

  // Mirrors the fields the API rejects when blank (non-nullable strings on CreateWebsiteRequest),
  // so the user finds out before submitting rather than from a failed save.
  const requiredField = (label: string) => ({
    validate: (value: string | null) => !!value?.trim() || `${label} is required`,
  });

  // Tell the user why the save failed rather than a bare "failed to save": server messages for
  // sending-config problems, per-field validation errors, and the common status codes.
  const onSaveError = (err: unknown) => {
    if (err instanceof ApiError) {
      const formFields = Object.keys(getValues()) as (keyof FormValues)[];
      const matchedFields = Object.entries(err.fieldErrors).filter(([key, messages]) => {
        // Server keys are PascalCase ("FaqUrl") or JSON paths ("$.faqUrl")
        const name = key.replace(/^\$\./, "");
        const field = formFields.find((f) => f.toLowerCase() === name.toLowerCase());
        if (field) setError(field, { type: "server", message: messages.join(" ") });
        return !!field;
      });

      if (matchedFields.length > 0) {
        addNotification({ variant: "error", primaryText: "Website not saved", secondaryText: "Please fix the highlighted fields and try again." });
        return;
      }
      if (err.status === 401) {
        addNotification({ variant: "error", primaryText: "Website not saved", secondaryText: "Your session has expired. Please log in again." });
        return;
      }
      if (err.status === 404) {
        addNotification({ variant: "error", primaryText: "Website not saved", secondaryText: "This website couldn't be found. It may have been deleted, so refresh the page and try again." });
        return;
      }
    }

    if (err instanceof Error && err.message && !err.message.startsWith("HTTP Error")) {
      addNotification({ variant: "error", primaryText: err.message });
    } else {
      saveHandlers.onError();
    }
  };

  const onSubmit = (data: FormValues) => {
    const trimmedGoogleTag = data.googleTag?.trim() ?? "";
    const trimmedDomain = data.sendingDomain?.trim() ?? "";
    const trimmedLocalPart = data.sendingFromLocalPart?.trim() ?? "";
    const trimmedFromName = data.sendingFromName?.trim() ?? "";
    const updatedWebsite = {
      ...website,
      ...data,
      googleTag: trimmedGoogleTag === "" ? null : trimmedGoogleTag,
      sendingDomain: trimmedDomain === NO_SENDING_DOMAIN ? null : trimmedDomain,
      sendingFromLocalPart: trimmedDomain === NO_SENDING_DOMAIN || trimmedLocalPart === "" ? null : trimmedLocalPart,
      sendingFromName: trimmedDomain === NO_SENDING_DOMAIN || trimmedFromName === "" ? null : trimmedFromName,
    };

    if (website !== undefined) {
      updateWebsite.mutate(updatedWebsite, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: onSaveError
      });
    } else {
      createWebsite.mutate(updatedWebsite, {
        onSuccess: (data) => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: onSaveError
      });
    }
  };

  const handleDelete = () => {
    deleteWebsite.mutate(website?.id!, {
      onSuccess: (data) => {
        deleteHandlers.onSuccess();
        setOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  };

  useEffect(() => {    
    reset({
      domain: website?.domain || "",
      resumeDomain: website?.resumeDomain || "",
      faqUrl: website?.faqUrl || null,
      googleTag: website?.googleTag || null,
      primaryColor: website?.primaryColor || "#035196",
      secondaryColor: website?.secondaryColor || null,
      accentColor: website?.accentColor || null,
      accentTextColor: website?.accentTextColor || null,
      bodyTextColor: website?.bodyTextColor || null,
      fontFamily: website?.fontFamily || "poppins",
      sendingDomain: website?.sendingDomain || NO_SENDING_DOMAIN,
      sendingFromLocalPart: website?.sendingFromLocalPart || null,
      sendingFromName: website?.sendingFromName || null,
    });
  }, [website, open])

  useEffect(() => {
    console.log("Primary Color Changed", primaryColor);
  }, [primaryColor]);

  return (
    <Modal width="max-w-md" open={open} setOpen={setOpen}>
      <h1 className="text-xl">{`${website ? "Edit" : "Add"} a Website`}</h1>
      <small className="text-gray-500"><i>
        {website ? "Edit the website details" : "Add a new website"}
      </i></small>

      <form className="" onSubmit={handleSubmit(onSubmit)}>
        <div className="my-4 grid grid-cols-1 gap-4">

          <TextInput
            label="Domain*"
            placeholder="https://sellavehicle.co.uk/"
            register={register}
            registerName="domain"
            registerOptions={requiredField("Domain")}
          />
          {errors.domain && (
            <small className="text-red-600 -mt-2">{errors.domain.message}</small>
          )}

          <TextInput
            label="Resume Domain*"
            placeholder="https://sellavehicle.co.uk/"
            register={register}
            registerName="resumeDomain"
            registerOptions={requiredField("Resume Domain")}
          />
          {errors.resumeDomain && (
            <small className="text-red-600 -mt-2">{errors.resumeDomain.message}</small>
          )}

          <TextInput
            label="FAQ URL*"
            placeholder="https://sellavehicle.co.uk/faqs"
            register={register}
            registerName="faqUrl"
            registerOptions={requiredField("FAQ URL")}
          />
          {errors.faqUrl && (
            <small className="text-red-600 -mt-2">{errors.faqUrl.message}</small>
          )}

          <TextInput
            label="Google Tag"
            placeholder="GTM-XXXXXXX"
            register={register}
            registerName="googleTag"
            registerOptions={{
              validate: (value: string | null) =>
                !value || value.trim() === "" || googleTagPattern.test(value.trim()) || "Invalid Google Tag format",
            }}
          />
          {errors.googleTag && (
            <small className="text-red-600 -mt-2">{errors.googleTag.message}</small>
          )}

          <TextInput
            label="Primary Color"
            placeholder="#035196"
            register={register}
            registerName="primaryColor"
            type="color"
            customClasses="h-12"
          />

          <TextInput
            label="Secondary Color (leave blank to use primary)"
            placeholder="#035196"
            register={register}
            registerName="secondaryColor"
            type="color"
            customClasses="h-12"
          />

          <TextInput
            label="Accent / CTA Button Color"
            placeholder="#FEC924"
            register={register}
            registerName="accentColor"
            type="color"
            customClasses="h-12"
          />

          <TextInput
            label="Accent Button Text Color"
            placeholder="#163465"
            register={register}
            registerName="accentTextColor"
            type="color"
            customClasses="h-12"
          />

          <TextInput
            label="Body Text Color"
            placeholder="#333333"
            register={register}
            registerName="bodyTextColor"
            type="color"
            customClasses="h-12"
          />

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Font Family</label>
            <select
              {...register("fontFamily")}
              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {FONT_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <hr className="my-2" />

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Email sending domain</label>
            <select
              {...register("sendingDomain")}
              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value={NO_SENDING_DOMAIN}>Wilma platform address (default)</option>
              {domainOptions.map((d) => (
                <option key={d.id} value={d.domain}>
                  {d.domain}{d.verified ? "" : " (pending verification)"}
                </option>
              ))}
              {orphanedDomain && (
                <option value={orphanedDomain}>{orphanedDomain} (no longer registered)</option>
              )}
            </select>
            <small className="text-gray-500">
              {domainOptions.length === 0
                ? "Add and verify a domain under \"Email sending domains\" on the Websites page to send from your own address."
                : selectedDomain && !selectedDomain.verified
                  ? "This domain isn't verified yet. Emails will come from the Wilma platform address, with your address as Reply-To, until it is."
                  : orphanedDomain
                    ? "This domain has been removed from your account. Emails will come from the Wilma platform address until you pick another."
                    : "Quote emails for this website will be sent from this domain once it is verified."}
            </small>
          </div>

          {sendingDomain && sendingDomain !== NO_SENDING_DOMAIN && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-500">From address</label>
                <div className="flex items-center mt-1">
                  <input
                    {...register("sendingFromLocalPart", {
                      validate: (value: string | null) =>
                        !value || value.trim() === "" || localPartPattern.test(value.trim()) || "Only letters, numbers, dots, hyphens and underscores",
                    })}
                    type="text"
                    placeholder="noreply"
                    className="flex-1 min-w-0 text-sm text-gray-900 border-1 border-gray-300 rounded-l-md shadow-sm"
                  />
                  <span className="inline-flex items-center px-3 py-2 text-sm text-gray-600 bg-gray-50 border border-l-0 border-gray-300 rounded-r-md">
                    @{sendingDomain}
                  </span>
                </div>
                {errors.sendingFromLocalPart ? (
                  <small className="text-red-600">{errors.sendingFromLocalPart.message}</small>
                ) : (
                  <small className="text-gray-500">
                    Emails will come from {(sendingFromLocalPart?.trim() || "noreply")}@{sendingDomain}
                  </small>
                )}
              </div>

              <TextInput
                label="From name"
                placeholder="Your company name"
                register={register}
                registerName="sendingFromName"
              />
            </>
          )}

        </div>

        <ButtonInput isSubmit={true} label={website ? "Update Website" : "Create Website"} classes="w-full mt-4" />

        {website && (
          <>
            <ButtonInput 
              isSubmit={false} 
              label="Delete Website" 
              onClick={() => setDeleteOpen(true)} 
              classes="bg-red-600 hover:bg-red-700 w-full mt-4" 
            />
            <ConfirmModal
              open={deleteOpen}
              setOpen={setDeleteOpen}
              title="Delete Website"
              message={`Are you sure you want to delete this website?`}
              confirmButtonText="Delete"
              onConfirm={handleDelete}
              isLoading={deleteWebsite.isLoading}
            />
          </>
        )}
      </form>
    </Modal>
  )
}
